/**
 * Die REGELN des Dokumentenmanagements — als pure Funktionen, damit sie ohne
 * Datenbank und ohne DOM pruefbar sind (stehende Entscheidung „duenner
 * Container, Logik in puren Funktionen"; das Projekt hat keine
 * DOM-Testumgebung, Komponenten sind also nicht testbar).
 *
 * Was ohne Supabase nicht zu haben ist, steht daneben in
 * `src/services/projektDokumenteService.ts`.
 *
 * Der Zufall ist ausdruecklich KEIN Teil dieser Datei: `storagePfad` bekommt
 * die UUID uebergeben, statt sie selbst zu ziehen. Sonst waere der Pfadaufbau
 * — die Grundlage der Storage-Policy — nicht testbar.
 */

import { DokumentKategorie, DokumentModul, ErzeugendesModul, ProjektDokument } from './types';
import type { RechteSicht } from '../../services/rechte/rechteService';
import { isAdminRole } from '../../services/projectLockLogic';

// ─── Kategorien ─────────────────────────────────────────────────────────────

export interface KategorieInfo {
  id: DokumentKategorie;
  label: string;
  /** Kurzer Satz fuer den Hinweis unter der Auswahl — was gehoert hier hin? */
  hinweis: string;
}

/**
 * Reihenfolge = Reihenfolge der Reiter. Sie folgt dem Arbeitsalltag und nicht
 * dem Alphabet: die eigenen Unterlagen des Koordinators zuerst, dann Plaene
 * und Vertraege als taegliches Geschaeft, `sonstiges` steht bewusst hinten.
 *
 * `eigene_unterlagen` ist auch von Hand waehlbar — etwa fuer das
 * unterschrieben zurueckgekommene Exemplar einer Fassung. Den Loeschschutz
 * tragen nur die von der Anwendung abgelegten Zeilen (`erzeugt_aus`), nicht
 * die Kategorie.
 */
export const KATEGORIEN: KategorieInfo[] = [
  {
    id: 'eigene_unterlagen',
    label: 'Eigene Unterlagen (SiGeKo)',
    hinweis: 'Von der App abgelegte Fassungen: Vorankündigung, SiGe-Plan, A+S-Plan, UfsA, Baustellenordnung, Handbuch, Einweisung',
  },
  { id: 'plaene', label: 'Pläne', hinweis: 'Ausführungs-, Lage- und Bestandspläne' },
  { id: 'vertraege', label: 'Verträge', hinweis: 'Verträge, Nachträge, Auftragsschreiben' },
  { id: 'genehmigungen', label: 'Genehmigungen', hinweis: 'Behördliche Bescheide, Auflagen, Anzeigen' },
  { id: 'gefaehrdungen', label: 'Gefährdungsbeurteilungen', hinweis: 'Beurteilungen der ausführenden Unternehmen' },
  { id: 'betriebsanweisung', label: 'Betriebsanweisungen', hinweis: 'Betriebsanweisungen, Sicherheitsdatenblätter' },
  { id: 'nachweise', label: 'Nachweise', hinweis: 'Freigaben, Prüfprotokolle, Bescheinigungen' },
  { id: 'protokolle', label: 'Protokolle', hinweis: 'Besprechungen, Aktenvermerke, Schriftwechsel' },
  { id: 'fotos', label: 'Fotos', hinweis: 'Baustellenfotos außerhalb der Begehungsberichte' },
  { id: 'sonstiges', label: 'Sonstiges', hinweis: 'Alles, was in keine der übrigen Schubladen passt' },
];

export const kategorieLabel = (id: string): string =>
  KATEGORIEN.find(k => k.id === id)?.label || 'Sonstiges';

// ─── Module ─────────────────────────────────────────────────────────────────

export interface ModulInfo {
  id: DokumentModul;
  label: string;
  /** Ist das Modul schon gebaut? `false` heisst: Zuordnung ja, Anzeige spaeter. */
  verfuegbar: boolean;
}

/**
 * Die Module, die auf die Ablage zugreifen.
 *
 * `baustellenordnung` meint seit dem Modul Baustellenordnung/HSE genau dieses
 * Modul und wird wieder angeboten (Entscheid E14; lange stand es auf
 * `verfuegbar: false`, weil es das Modul nicht gab). Die Baustellenordnung als
 * Kapitel des SiGe-Handbuchs bleibt bei `handbuch`. Die Reihenfolge folgt der
 * Kachelkette im ProjectModuleGrid.
 */
export const MODULE: ModulInfo[] = [
  { id: 'sigeplan', label: 'SiGe-Plan', verfuegbar: true },
  { id: 'unterlage', label: 'Unterlage für spätere Arbeiten', verfuegbar: true },
  { id: 'baustellenordnung', label: 'Baustellenordnung / HSE', verfuegbar: true },
  { id: 'einweisung', label: 'Einweisung SiGe-Plan', verfuegbar: true },
  { id: 'begehung', label: 'Begehungsbericht', verfuegbar: true },
  { id: 'vorankuendigung', label: 'Vorankündigung', verfuegbar: true },
  { id: 'planungsphase', label: 'Planungsphase', verfuegbar: true },
  { id: 'handbuch', label: 'SiGe-Handbuch', verfuegbar: true },
  { id: 'koordinierung', label: 'Gewerke-Überschneidung', verfuegbar: true },
  // Angehaengt statt an der Kachelstelle eingeordnet: `toggleModul` gibt die
  // Reihenfolge von MODULE zurueck, ein Einschub sortierte deshalb bei der
  // naechsten Aenderung jede gespeicherte Zuordnung um. Der CHECK von
  // `project_documents` kennt 'asplan' seit Migration Nr. 41.
  { id: 'asplan', label: 'A+S-Plan (kontaminierte Bereiche)', verfuegbar: true },
];

/**
 * Wie `kategorieLabel` mit einem lesbaren Rueckfall statt der rohen Kennung:
 * ein unbekannter Wert soll im Chip nicht als `baustellenordnung_v2`
 * dastehen. Er kann heute nur aus einem Bestand kommen, den ein spaeterer
 * CHECK-Wert erzeugt hat — Grund genug, ihn lesbar zu halten.
 */
export const modulLabel = (id: string): string =>
  MODULE.find(m => m.id === id)?.label || ERZEUGER_OHNE_MODUL[id] || 'Weiteres Modul';

/**
 * Erzeugende Module ohne Modul-Schlagwort der Ablage (kein Eintrag in MODULE),
 * z. B. die SiFa-Gefaehrdungsbeurteilung (`erzeugt_aus = 'gbu'`, Nr. 167).
 */
const ERZEUGER_OHNE_MODUL: Readonly<Record<string, string>> = {
  gbu: 'Gefährdungsbeurteilung (SiFa)',
};

/** Ein Modul an- oder abwaehlen. Reihenfolge bleibt die von MODULE. */
export const toggleModul = (aktuell: DokumentModul[], modul: DokumentModul): DokumentModul[] => {
  const gesetzt = new Set(aktuell);
  if (gesetzt.has(modul)) gesetzt.delete(modul);
  else gesetzt.add(modul);
  return MODULE.filter(m => gesetzt.has(m.id)).map(m => m.id);
};

// ─── Datei-Prüfung ──────────────────────────────────────────────────────────

/**
 * 50 MB — dieselbe Zahl steht am Bucket (`file_size_limit`, Migration Nr. 37).
 * Die Pruefung hier ist Bequemlichkeit (sofortige, verstaendliche Absage), der
 * Riegel ist der Bucket. Wer eine der beiden Zahlen aendert, aendert beide.
 */
export const MAX_DOKUMENT_BYTES = 52428800;

/**
 * Erlaubte MIME-Typen, Spiegel von `allowed_mime_types` am Bucket.
 *
 * Bewusst eine Liste und keine Endungspruefung: der Browser meldet den
 * Content-Type, und genau den prueft auch der Bucket. Eine Endung sagt nichts
 * darueber, was tatsaechlich hochgeladen wird.
 */
export const ERLAUBTE_DOKUMENT_TYPEN = [
  'application/pdf',
  'image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/tiff',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'text/plain', 'text/csv',
  'application/zip',
  'image/vnd.dwg', 'application/acad', 'application/dxf',
];

/**
 * Endungen, deren Content-Type der Browser haeufig gar nicht kennt (er meldet
 * dann `''` oder `application/octet-stream`). Fuer sie gilt die Endung als
 * Nachweis — der Bucket bekommt in dem Fall den passenden Typ mitgegeben.
 *
 * `pdf`/`docx` stehen hier fuer die von der Anwendung ERZEUGTEN Dateien: ein
 * `Blob` aus jsPDF oder dem Word-Generator traegt nicht immer einen Typ, und
 * ohne Typ schickte storage-js `text/plain` — den der Bucket ablehnt.
 */
const ENDUNG_TYP: Record<string, string> = {
  dwg: 'image/vnd.dwg',
  dxf: 'application/dxf',
  heic: 'image/heic',
  tif: 'image/tiff',
  tiff: 'image/tiff',
  pdf: 'application/pdf',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
};

/** Dateiendung aus dem Namen, klein, ohne Punkt. Ohne erkennbare Endung: 'dat'. */
export const dateiEndung = (dateiname: string): string => {
  const treffer = /\.([a-zA-Z0-9]{1,8})$/.exec((dateiname || '').trim());
  return treffer ? treffer[1].toLowerCase() : 'dat';
};


/**
 * Die Dateiart fuer die Anzeige — welches Sinnbild und welches Kuerzel steht
 * links neben dem Dokument?
 *
 * Rein optisch: sie entscheidet nichts, sie hilft nur beim Wiederfinden. Genau
 * deshalb hat hier die ENDUNG Vorrang vor dem Content-Type — der Anwender
 * sieht den Dateinamen, nicht den MIME-Typ, und ein Dokument, das „.docx"
 * heisst, muss als Word erscheinen, auch wenn der Bucket
 * `application/octet-stream` gespeichert hat. Fuer die Upload-Pruefung gilt
 * weiterhin der umgekehrte Weg (`inhaltstyp`): dort ist der gemeldete Typ der
 * Nachweis, weil ihn auch der Bucket prueft.
 */
export type DateiArtId = 'pdf' | 'word' | 'excel' | 'powerpoint' | 'bild' | 'cad' | 'archiv' | 'text' | 'sonstige';

export interface DateiArtInfo {
  id: DateiArtId;
  /** Zwei bis vier Zeichen unter dem Sinnbild — „PDF", „DOC", „XLS". */
  kuerzel: string;
  /** Klartext fuer den Tooltip. */
  label: string;
}

const DATEI_ARTEN: Record<DateiArtId, DateiArtInfo> = {
  pdf: { id: 'pdf', kuerzel: 'PDF', label: 'PDF-Dokument' },
  word: { id: 'word', kuerzel: 'DOC', label: 'Word-Dokument' },
  excel: { id: 'excel', kuerzel: 'XLS', label: 'Tabelle' },
  powerpoint: { id: 'powerpoint', kuerzel: 'PPT', label: 'Präsentation' },
  bild: { id: 'bild', kuerzel: 'BILD', label: 'Bild' },
  cad: { id: 'cad', kuerzel: 'CAD', label: 'CAD-Zeichnung' },
  archiv: { id: 'archiv', kuerzel: 'ZIP', label: 'Archiv' },
  text: { id: 'text', kuerzel: 'TXT', label: 'Textdatei' },
  sonstige: { id: 'sonstige', kuerzel: 'DATEI', label: 'Datei' },
};

/** Endung → Art. Die Liste deckt ab, was der Bucket ueberhaupt annimmt. */
const ENDUNG_ART: Record<string, DateiArtId> = {
  pdf: 'pdf',
  doc: 'word', docx: 'word', rtf: 'word', odt: 'word',
  xls: 'excel', xlsx: 'excel', csv: 'excel', ods: 'excel',
  ppt: 'powerpoint', pptx: 'powerpoint', odp: 'powerpoint',
  jpg: 'bild', jpeg: 'bild', png: 'bild', webp: 'bild', heic: 'bild', gif: 'bild',
  tif: 'bild', tiff: 'bild', bmp: 'bild',
  dwg: 'cad', dxf: 'cad', dwf: 'cad', ifc: 'cad',
  zip: 'archiv', rar: 'archiv', '7z': 'archiv',
  txt: 'text', md: 'text',
};

/** Content-Type → Art, wenn die Endung nichts hergibt. */
const artAusMime = (mime: string): DateiArtId | null => {
  if (!mime) return null;
  if (mime.includes('pdf')) return 'pdf';
  if (mime.startsWith('image/')) return 'bild';
  if (mime.includes('wordprocessingml') || mime === 'application/msword') return 'word';
  if (mime.includes('spreadsheetml') || mime.includes('excel') || mime.includes('csv')) return 'excel';
  if (mime.includes('presentationml') || mime.includes('powerpoint')) return 'powerpoint';
  if (mime.includes('dwg') || mime.includes('dxf') || mime.includes('acad')) return 'cad';
  if (mime.includes('zip') || mime.includes('compressed')) return 'archiv';
  if (mime.startsWith('text/')) return 'text';
  return null;
};

/**
 * Die Art einer abgelegten Datei. Ohne erkennbare Art: `sonstige` — nie eine
 * Fehlermeldung, die Liste muss auch fuer Unbekanntes eine Zeile zeichnen.
 */
export const dateiArt = (dateiname: string, mime?: string | null): DateiArtInfo => {
  const ausEndung = ENDUNG_ART[dateiEndung(dateiname)];
  if (ausEndung) return DATEI_ARTEN[ausEndung];
  const ausMime = artAusMime((mime || '').trim().toLowerCase());
  return DATEI_ARTEN[ausMime || 'sonstige'];
};

/**
 * Der Content-Type, mit dem hochgeladen wird.
 *
 * Der Browser meldet fuer CAD- und HEIC-Dateien oft nichts Brauchbares. Ohne
 * diese Auffuellung lehnte der Bucket sie ab (`allowed_mime_types`), und der
 * Anwender saehe eine Fehlermeldung ueber einen Typ, den er nie gesetzt hat.
 */
export const inhaltstyp = (dateiname: string, gemeldet?: string | null): string | undefined => {
  const typ = (gemeldet || '').trim().toLowerCase();
  if (typ && typ !== 'application/octet-stream' && ERLAUBTE_DOKUMENT_TYPEN.includes(typ)) return typ;
  const ausEndung = ENDUNG_TYP[dateiEndung(dateiname)];
  if (ausEndung) return ausEndung;
  return typ || undefined;
};

/**
 * Prueft eine Datei VOR dem Upload. Rueckgabe: Klartext-Absage oder `null`.
 *
 * Warum als Text und nicht als Code: die Meldung geht unveraendert an den
 * Anwender, und sie soll sagen, was zu tun ist — nicht, welche Regel gegriffen
 * hat.
 */
export const pruefeDatei = (datei: { name: string; size: number; type?: string }): string | null => {
  if (!datei.name || !datei.name.trim()) return 'Die Datei hat keinen Namen.';
  if (datei.size <= 0) return `„${datei.name}" ist leer und lässt sich nicht ablegen.`;
  if (datei.size > MAX_DOKUMENT_BYTES) {
    return `„${datei.name}" ist ${formatBytes(datei.size)} groß. Erlaubt sind bis zu `
      + `${formatBytes(MAX_DOKUMENT_BYTES)} — bitte die Datei verkleinern oder aufteilen.`;
  }
  const typ = inhaltstyp(datei.name, datei.type);
  if (!typ || !ERLAUBTE_DOKUMENT_TYPEN.includes(typ)) {
    return `Der Dateityp von „${datei.name}" ist für die Ablage nicht vorgesehen. `
      + 'Erlaubt sind PDF, Bilder, Office-Dateien, Text/CSV, ZIP und CAD-Pläne.';
  }
  return null;
};

// ─── Namen und Pfade ────────────────────────────────────────────────────────

/**
 * Der Pfad im Bucket `projekt-dokumente`.
 *
 * Das Schema `projekt/<projectId>/<uuid>.<ext>` ist NICHT Kosmetik: die
 * Storage-Policy liest die Projekt-Id aus `(storage.foldername(name))[2]` und
 * fragt damit `can_access_project`. Wer dieses Schema aendert, muss die
 * Migration mitaendern — sonst faellt entweder der Zugriff aus oder, schlimmer,
 * die Policy passt auf nichts mehr und laesst alles durch.
 *
 * Die UUID statt des Originalnamens: zwei gleichnamige Plaene duerfen sich
 * nicht ueberschreiben, und ein Dateiname im Bucket soll nicht verraten, was
 * drin steht. Der Originalname steht in der Zeile.
 */
export const storagePfad = (projectId: string, dateiname: string, uuid: string): string =>
  `projekt/${projectId}/${uuid}.${dateiEndung(dateiname)}`;

/** Vorschlag fuer den Titel: der Dateiname ohne Endung, Unterstriche als Leerzeichen. */
export const titelAusDateiname = (dateiname: string): string => {
  const ohneEndung = (dateiname || '').replace(/\.[a-zA-Z0-9]{1,8}$/, '');
  const lesbar = ohneEndung.replace(/[_]+/g, ' ').replace(/\s{2,}/g, ' ').trim();
  return lesbar || dateiname || 'Dokument';
};

/**
 * Ordnerpfad normalisieren: keine fuehrenden/schliessenden Schraegstriche,
 * keine doppelten, kein Leerraum um die Stufen.
 *
 * Ohne das entstehen „Geruest", „Geruest/" und „/Geruest" als drei verschiedene
 * Ordner — und die Ablage sieht aus, als haette jemand dreimal dasselbe
 * angelegt.
 */
export const normalisiereOrdner = (eingabe: string): string =>
  (eingabe || '')
    .split('/')
    .map(t => t.trim())
    .filter(Boolean)
    .join('/');

/** Schlagworte aus einem Eingabefeld: Komma oder Semikolon trennt, leer faellt weg, ohne Dubletten. */
export const parseTags = (eingabe: string): string[] => {
  const roh = (eingabe || '').split(/[,;]/).map(t => t.trim()).filter(Boolean);
  const gesehen = new Set<string>();
  const raus: string[] = [];
  for (const t of roh) {
    const schluessel = t.toLowerCase();
    if (gesehen.has(schluessel)) continue;
    gesehen.add(schluessel);
    raus.push(t);
  }
  return raus;
};

/** Byte-Zahl als lesbare Groesse. Bewusst mit Komma — deutsche Oberflaeche. */
export const formatBytes = (bytes: number): string => {
  const b = Number.isFinite(bytes) && bytes > 0 ? Math.round(bytes) : 0;
  if (b < 1024) return `${b} B`;
  // Kilobyte ohne Nachkommastelle — dort interessiert die Zahl niemanden
  // genauer. `toFixed(0)` liefert nie einen Punkt, deshalb steht hier auch
  // keine Komma-Ersetzung (die stand hier einmal und war wirkungslos).
  if (b < 1024 * 1024) return `${Math.round(b / 1024)} KB`;
  if (b < 1024 * 1024 * 1024) return `${(b / (1024 * 1024)).toFixed(1).replace('.', ',')} MB`;
  return `${(b / (1024 * 1024 * 1024)).toFixed(2).replace('.', ',')} GB`;
};

// ─── Gültigkeit ─────────────────────────────────────────────────────────────

export type AblaufStatus = 'abgelaufen' | 'laeuft_ab' | 'gueltig';

/** Ab wie vielen Tagen vor dem Ablauf gewarnt wird. */
export const ABLAUF_WARNUNG_TAGE = 30;

/**
 * Steht dieser Nachweis noch?
 *
 * ISO-Datumsstrings (YYYY-MM-DD) sind lexikografisch vergleichbar — fuer
 * „abgelaufen" braucht es kein Date-Parsing. Nur die Vorwarnzeit rechnet mit
 * Tagen, und dafuer genuegt der Tagesabstand in UTC (beide Werte sind reine
 * Datumsangaben, es gibt keine Uhrzeit, die eine Zone verschieben koennte).
 *
 * `null` heisst: Das Dokument laeuft nicht ab. Das ist der Normalfall und
 * ausdruecklich kein Mangel.
 */
export const ablaufStatus = (gueltigBis: string | null | undefined, heute: string): AblaufStatus | null => {
  const bis = (gueltigBis || '').slice(0, 10);
  if (!bis) return null;
  if (bis < heute) return 'abgelaufen';
  const tage = Math.round(
    (Date.parse(`${bis}T00:00:00Z`) - Date.parse(`${heute}T00:00:00Z`)) / 86400000,
  );
  return Number.isFinite(tage) && tage <= ABLAUF_WARNUNG_TAGE ? 'laeuft_ab' : 'gueltig';
};

/** Wie viele Dokumente abgelaufen sind oder demnaechst ablaufen. */
export const ablaufWarnungen = (dokumente: ProjektDokument[], heute: string): {
  abgelaufen: ProjektDokument[];
  laufenAb: ProjektDokument[];
} => {
  const abgelaufen: ProjektDokument[] = [];
  const laufenAb: ProjektDokument[] = [];
  for (const d of dokumente) {
    const status = ablaufStatus(d.gueltig_bis, heute);
    if (status === 'abgelaufen') abgelaufen.push(d);
    else if (status === 'laeuft_ab') laufenAb.push(d);
  }
  return { abgelaufen, laufenAb };
};

// ─── Filtern, Sortieren, Gruppieren ─────────────────────────────────────────

export type DokumentSortierung = 'neueste' | 'aelteste' | 'name' | 'groesse' | 'ablauf';

export interface DokumentFilter {
  /** Freitext ueber Titel, Dateiname, Beschreibung, Ordner, Stand und Schlagworte. */
  suche?: string;
  /** `null`/undefined = alle Kategorien. */
  kategorie?: DokumentKategorie | null;
  /** `null`/undefined = ohne Modulbezug filtern. */
  modul?: DokumentModul | null;
  /** Nur, was abgelaufen ist oder demnaechst ablaeuft. */
  nurAblaufend?: boolean;
}

const suchtext = (d: ProjektDokument): string =>
  [d.titel, d.dateiname, d.beschreibung || '', d.ordner, d.stand || '', ...d.tags]
    .join(' ')
    .toLowerCase();

/**
 * Der Filter der Ablage.
 *
 * Alle Kriterien wirken UND-verknuepft; die Freitextsuche zerlegt die Eingabe
 * in Woerter, die alle vorkommen muessen. „geruest freigabe" findet damit auch
 * „Freigabe Gerüst BA 2", was eine reine Teilstringsuche nicht taete.
 */
export const filterDokumente = (
  dokumente: ProjektDokument[],
  filter: DokumentFilter,
  heute: string,
): ProjektDokument[] => {
  const worte = (filter.suche || '').toLowerCase().split(/\s+/).filter(Boolean);
  return dokumente.filter(d => {
    if (filter.kategorie && d.kategorie !== filter.kategorie) return false;
    if (filter.modul && !d.module.includes(filter.modul)) return false;
    if (filter.nurAblaufend) {
      const status = ablaufStatus(d.gueltig_bis, heute);
      if (status !== 'abgelaufen' && status !== 'laeuft_ab') return false;
    }
    if (worte.length) {
      const text = suchtext(d);
      if (!worte.every(w => text.includes(w))) return false;
    }
    return true;
  });
};

/**
 * Sortierung. `ablauf` stellt die dringenden Nachweise nach vorn und schiebt
 * alles ohne Ablaufdatum ans Ende — sonst stuenden die harmlosen Dokumente
 * zwischen den ablaufenden.
 */
export const sortiereDokumente = (
  dokumente: ProjektDokument[],
  sortierung: DokumentSortierung,
): ProjektDokument[] => {
  const kopie = [...dokumente];
  switch (sortierung) {
    case 'aelteste':
      return kopie.sort((a, b) => a.created_at.localeCompare(b.created_at));
    case 'name':
      return kopie.sort((a, b) => a.titel.localeCompare(b.titel, 'de'));
    case 'groesse':
      return kopie.sort((a, b) => b.groesse_bytes - a.groesse_bytes);
    case 'ablauf':
      return kopie.sort((a, b) => {
        const av = (a.gueltig_bis || '').slice(0, 10);
        const bv = (b.gueltig_bis || '').slice(0, 10);
        if (av && bv) return av.localeCompare(bv);
        if (av) return -1;
        if (bv) return 1;
        return b.created_at.localeCompare(a.created_at);
      });
    case 'neueste':
    default:
      return kopie.sort((a, b) => b.created_at.localeCompare(a.created_at));
  }
};

/**
 * Wie viele Zeilen ein Ordner auf einmal zeigt — und zugleich die Grenze,
 * bis zu der eine Ablage als klein gilt und alle Ordner offen starten.
 *
 * Ohne Grenze stand ein Ordner mit 100 Dateien in voller Laenge da, und alles
 * darunter war nur noch durch langes Scrollen erreichbar.
 */
export const DOKUMENTE_JE_SEITE = 20;

export interface OrdnerKnoten {
  /** Name dieser Stufe, z. B. „BA 2" in „Gerüst/BA 2". Leer nur an der Wurzel. */
  name: string;
  /** Voller Pfad — der Schluessel fuer Auf-/Zuklappen. Leer = Wurzel. */
  pfad: string;
  /** Dokumente direkt in diesem Ordner, in der Reihenfolge der Eingabe. */
  dokumente: ProjektDokument[];
  /** Unterordner, nach Name sortiert. */
  unterordner: OrdnerKnoten[];
  /** Dokumente in diesem Ordner UND allen darunter — die Zahl am Kopf. */
  gesamt: number;
}

/**
 * Die Ablage als Ordnerbaum. Die Wurzel traegt die ordnerlosen Dokumente,
 * „Gerüst/BA 2" haengt als Unterordner unter „Gerüst" — auch wenn in
 * „Gerüst" selbst kein Dokument liegt.
 *
 * Der Pfad wird hier noch einmal normalisiert: ein Altbestand mit „Gerüst/"
 * landete sonst als eigener Ordner neben „Gerüst".
 *
 * Die Reihenfolge INNERHALB eines Ordners bleibt die der Eingabe — das
 * Sortieren ist Sache von `sortiereDokumente` und soll nicht zweimal
 * stattfinden.
 */
export const baueOrdnerBaum = (dokumente: ProjektDokument[]): OrdnerKnoten => {
  const wurzel: OrdnerKnoten = { name: '', pfad: '', dokumente: [], unterordner: [], gesamt: 0 };
  const knoten = new Map<string, OrdnerKnoten>([['', wurzel]]);
  const hole = (pfad: string): OrdnerKnoten => {
    const da = knoten.get(pfad);
    if (da) return da;
    const schnitt = pfad.lastIndexOf('/');
    const eltern = hole(schnitt < 0 ? '' : pfad.slice(0, schnitt));
    const neu: OrdnerKnoten = { name: pfad.slice(schnitt + 1), pfad, dokumente: [], unterordner: [], gesamt: 0 };
    eltern.unterordner.push(neu);
    knoten.set(pfad, neu);
    return neu;
  };
  for (const d of dokumente) hole(normalisiereOrdner(d.ordner)).dokumente.push(d);

  const ordne = (k: OrdnerKnoten): number => {
    k.unterordner.sort((a, b) => a.name.localeCompare(b.name, 'de'));
    k.gesamt = k.unterordner.reduce((summe, u) => summe + ordne(u), k.dokumente.length);
    return k.gesamt;
  };
  ordne(wurzel);
  return wurzel;
};

/** Alle Ordnerpfade unterhalb des Knotens (ohne ihn selbst) — fuer „Alle aufklappen". */
export const ordnerPfade = (k: OrdnerKnoten): string[] =>
  k.unterordner.flatMap(u => [u.pfad, ...ordnerPfade(u)]);

/** Alle Dokumente im Ast, Unterordner eingeschlossen. */
export const dokumenteImAst = (k: OrdnerKnoten): ProjektDokument[] =>
  [...k.dokumente, ...k.unterordner.flatMap(dokumenteImAst)];

/**
 * Der Ordner selbst und alle, die ueber ihm liegen: „Gerüst/BA 2" →
 * ["Gerüst", "Gerüst/BA 2"]. Wer ein Dokument nach dem Ablegen sehen soll,
 * muss den ganzen Weg dorthin offen haben, nicht nur den letzten Ordner.
 */
export const ordnerKette = (ordner: string): string[] => {
  const stufen = normalisiereOrdner(ordner).split('/').filter(Boolean);
  return stufen.map((_, i) => stufen.slice(0, i + 1).join('/'));
};

/** Alle im Projekt schon benutzten Ordner — Vorschlagsliste beim Hochladen. */
export const ordnerVorschlaege = (dokumente: ProjektDokument[]): string[] => {
  const gesehen = new Set<string>();
  for (const d of dokumente) if (d.ordner) gesehen.add(d.ordner);
  return [...gesehen].sort((a, b) => a.localeCompare(b, 'de'));
};

/** Wie viele Dokumente je Kategorie — die Zahlen an den Reitern. */
export const zaehleKategorien = (dokumente: ProjektDokument[]): Record<string, number> => {
  const zahlen: Record<string, number> = {};
  for (const d of dokumente) zahlen[d.kategorie] = (zahlen[d.kategorie] || 0) + 1;
  return zahlen;
};

/**
 * Admin-Schloss setzen und aufheben, gesperrte Dokumente sehen: Admins und wer
 * das Recht „„Nur Admin“-Dokumente“ hat (Rollensystem). Dieselbe Grenze zieht
 * die Datenbank (Nr. 154: Policy auf `project_documents`, `dokument_gesperrt`
 * fuer den Bucket) — hier entscheidet sie nur die Anzeige. Admins haben jedes
 * Recht; ohne Migration gilt die Systemrolle, also wie bisher nur Admins.
 */
export const darfAdminSchloss = (rechte: Pick<RechteSicht, 'hat'>): boolean =>
  rechte.hat('dokumente.nur_admin');

/**
 * Wie viele Dokumente das Admin-Schloss tragen.
 *
 * Nur fuer die Verwaltung eine sinnvolle Zahl (darfAdminSchloss) — allen
 * anderen liefert der Server gesperrte Zeilen gar nicht erst, die Zahl waere
 * dort immer 0. Genau deshalb ist sie brauchbar: sie sagt einem Admin, wie
 * viel von dem, was er sieht, seine Kollegen NICHT sehen.
 */
export const zaehleGesperrte = (dokumente: ProjektDokument[]): number =>
  dokumente.reduce((zahl, d) => (d.nur_admin ? zahl + 1 : zahl), 0);

/** Belegter Speicher der Ablage in Bytes. */
export const speicherGesamt = (dokumente: ProjektDokument[]): number =>
  dokumente.reduce((summe, d) => summe + (Number.isFinite(d.groesse_bytes) ? d.groesse_bytes : 0), 0);

// ─── Erzeugte Unterlagen ────────────────────────────────────────────────────

/**
 * ═══ SPALTE ZUERST, HEURISTIK NUR IM ALTSCHEMA ═══════════════════════════
 *
 * Die urspruengliche Abgrenzung der Ablage (Aug. 2026) lautete: was die
 * Anwendung selbst erzeugt, liegt NICHT in `project_documents`. Nur deshalb
 * durfte das Archivieren anbieten, die Ablage komplett zu raeumen. Seit der
 * zentralen Ablage (Plan Stufe 1A) legen die Module ihre Fassungen genau hier
 * ab — als Nachweis: Rev. 3.0 muss nach der Freigabe von Rev. 4.0 noch
 * belegbar sein. Das Raeumen beim Archivieren vernichtete solche Nachweise.
 *
 * Die Kennzeichnung ist die Spalte `erzeugt_aus` (Nr. 160/161): gesetzt =
 * von der Anwendung abgelegt, NULL = hochgeladen. Sie entscheidet allein,
 * sobald sie da ist. Nur wenn das Feld FEHLT (`undefined`, Altschema vor der
 * Migration) greift der Rueckfall: der Fingerabdruck der A+S-Fassungen, die
 * vor der Spalte abgelegt wurden — Modul `asplan` und der feste Unterordner
 * aus `planAblageEntwurf`. Die Ordnerkonstante steht hier ABSICHTLICH noch
 * einmal und wird nicht aus `asplan/ablage/anlagen.ts` importiert: die Ablage
 * ist der Kern, das A+S-Modul ihr Nutzer, und ein Import in Gegenrichtung
 * baute einen Kreis, der beim naechsten Laufzeit-Import in `anlagen.ts` zur
 * TDZ-Falle wuerde. Dass beide Werte gleich bleiben, prueft der Test.
 */
const ASPLAN_ABLAGE_ORDNER = 'A+S-Plan';

/** Schlagwort, das `planAblageEntwurf` jeder Fassung mitgibt. */
const ASPLAN_ABLAGE_TAG = 'a+s-plan';

/** Die Felder, an denen eine erzeugte Unterlage zu erkennen ist. */
export type ErzeugtMerkmale = Pick<ProjektDokument, 'kategorie' | 'ordner' | 'module' | 'tags' | 'erzeugt_aus'>;

/**
 * Der Rueckfall fuer das Altschema: eine A+S-Fassung, abgelegt bevor es die
 * Spalte gab. Modul `asplan` ist Pflicht — ein fremder Plan im selben Ordner
 * ohne Modulbezug ist ein fremder Plan. Darueber hinaus genuegt EINES von
 * beiden: der feste Unterordner (auch tiefer darunter) oder, falls jemand die
 * Fassung umgeraeumt hat, Kategorie „Plaene" mit dem Schlagwort des Moduls.
 * Lieber eine Zeile zu viel behalten als einen Revisionsnachweis loeschen.
 */
const istAsplanFassungNachFingerabdruck = (dok: Omit<ErzeugtMerkmale, 'erzeugt_aus'>): boolean => {
  if (!Array.isArray(dok.module) || !dok.module.includes('asplan')) return false;
  const ordner = normalisiereOrdner(dok.ordner).toLowerCase();
  const fest = ASPLAN_ABLAGE_ORDNER.toLowerCase();
  if (ordner === fest || ordner.startsWith(`${fest}/`)) return true;
  return dok.kategorie === 'plaene'
    && Array.isArray(dok.tags)
    && dok.tags.some(t => (t || '').trim().toLowerCase() === ASPLAN_ABLAGE_TAG);
};

/**
 * Welches Modul dieses Dokument abgelegt hat — `null` fuer eine hochgeladene
 * Datei.
 *
 * `erzeugt_aus` entscheidet, sobald die Spalte vorhanden ist (auch mit NULL:
 * dann ist es hochgeladen, und die Heuristik hat nichts mehr zu sagen — die
 * Migration kennzeichnet den A+S-Altbestand nach). Nur ein FEHLENDES Feld
 * (`undefined`) ist das Altschema, und nur dort greift der Fingerabdruck.
 */
export const erzeugtVon = (dok: ErzeugtMerkmale): ErzeugendesModul | null => {
  if (dok.erzeugt_aus !== undefined) return dok.erzeugt_aus || null;
  return istAsplanFassungNachFingerabdruck(dok) ? 'asplan' : null;
};

/**
 * Ist dieses Dokument eine von der Anwendung abgelegte Unterlage und keine
 * fremde, hochgeladene Datei? Siehe `erzeugtVon`.
 */
export const istErzeugteUnterlage = (dok: ErzeugtMerkmale): boolean => erzeugtVon(dok) !== null;

/** Wie viele Dokumente der Liste erzeugte Unterlagen sind. */
export const zaehleErzeugte = (dokumente: ProjektDokument[]): number =>
  dokumente.reduce((zahl, d) => (istErzeugteUnterlage(d) ? zahl + 1 : zahl), 0);

/** Das kleine Kennzeichen an der Zeile. */
export const ERZEUGT_KENNZEICHEN = 'von der App abgelegt';

/** Der Tooltip zum Kennzeichen. */
export const ERZEUGT_HINWEIS =
  'Diese Fassung hat die App beim Freigeben oder Versenden abgelegt. '
  + 'Sie ist ein Nachweis und lässt sich nur von der Verwaltung löschen.';

/**
 * Darf diese Person erzeugte Fassungen loeschen? Dieselbe Regel wie die
 * Policy (Nr. 160/161): Verwaltung (`admin`/`super_admin`, `is_admin()`) —
 * oder ein Uebungsprojekt, in dem alles geloescht werden darf. Eine
 * ANZEIGE-Entscheidung und Vorabpruefung; der Riegel ist die Policy.
 */
export const darfErzeugteLoeschen = (
  systemrolle: string | null | undefined,
  istUebungsprojekt = false,
): boolean => isAdminRole(systemrolle) || istUebungsprojekt === true;

/**
 * Warum dieses Dokument NICHT geloescht werden darf — oder `null`, wenn es
 * geloescht werden darf.
 *
 * Als Text, weil derselbe Satz an zwei Stellen steht: am ausgegrauten Knopf
 * der Liste und als Absage von `loescheDokument`, bevor die Datei angefasst
 * wird. Die Datenbank wiese die Zeile ohnehin ab — aber erst NACH der Datei
 * (Reihenfolge „erst Datei, dann Zeile"), und dann bliebe ein Nachweis ohne
 * Datei stehen.
 */
export const erzeugteLoeschSperre = (dok: ErzeugtMerkmale, darfErzeugte: boolean): string | null => {
  const modul = erzeugtVon(dok);
  if (!modul || darfErzeugte) return null;
  return `Diese Fassung hat die App abgelegt (${modulLabel(modul)}). Sie ist ein Nachweis und `
    + 'lässt sich nur von der Verwaltung löschen.';
};

// ─── Archivierung ───────────────────────────────────────────────────────────

/**
 * Der Text der Archiv-Rueckfrage.
 *
 * Er nennt Anzahl UND Speicher, weil beide Zahlen verschiedene Leute
 * ueberzeugen — und er nennt ausdruecklich, was NICHT geloescht wird. Ohne
 * diesen Satz liest sich die Frage wie „soll ich das Projekt ausraeumen?", und
 * dann klickt jeder auf „behalten".
 *
 * `anzahl` und `bytes` sind das, was zur Entscheidung steht — ohne die
 * erzeugten Unterlagen. Liegen NUR solche in der Ablage, darf der Satz nicht
 * „keine Dokumente" lauten: der Anwender weiss, dass seine Fassungen dort
 * liegen, und hielte die Anzeige fuer falsch.
 */
export const archivFrageText = (anzahl: number, bytes: number, erzeugte = 0): string => {
  if (anzahl <= 0) {
    if (erzeugte > 0) {
      return `In der Dokumentenablage dieses Projekts ${erzeugte === 1
        ? 'liegt nur 1 von der App abgelegte Unterlage; sie bleibt'
        : `liegen nur ${erzeugte} von der App abgelegte Unterlagen; sie bleiben`} erhalten.`;
    }
    return 'Zu diesem Projekt liegen keine Dokumente in der Ablage.';
  }
  const doks = anzahl === 1 ? '1 Dokument' : `${anzahl} Dokumente`;
  return `In der Dokumentenablage dieses Projekts liegen ${doks} (${formatBytes(bytes)}).`;
};

/**
 * Der Satz zu den erzeugten Unterlagen, die das Raeumen ausnimmt. `null`,
 * wenn es keine gibt — dann soll auch kein Satz dastehen, der eine Ausnahme
 * behauptet, die gar nicht greift.
 */
export const erzeugteBleibenText = (erzeugte: number): string | null => {
  if (erzeugte <= 0) return null;
  return erzeugte === 1
    ? '1 von der App abgelegte Unterlage bleibt erhalten.'
    : `${erzeugte} von der App abgelegte Unterlagen bleiben erhalten.`;
};

/**
 * Was beim Loeschen ausdruecklich stehen bleibt. Steht als eigene Liste hier
 * und nicht als Fliesstext in der Komponente, damit derselbe Wortlaut auch in
 * einer spaeteren zweiten Bestaetigung benutzt werden kann.
 */
export const ARCHIV_BLEIBT_ERHALTEN = [
  'Begehungsberichte und ihr Archiv',
  'SiGe-Pläne samt Versionen',
  'Unterlage für spätere Arbeiten',
  'Vorankündigungen',
  'Einweisungsblätter',
  'Rechnungen und Gutschriften',
  'Von der App abgelegte eigene Unterlagen (Fassungen aus Vorankündigung, SiGe-Plan, A+S-Plan, UfsA, Baustellenordnung, Handbuch, Einweisung)',
];
